import React, { useEffect, useMemo } from 'react';
import { Font, Image, Text, View, StyleSheet } from '@react-pdf/renderer';
import { logo_dof } from 'components/base/images';
import moment from 'moment';
import { calculateAge } from 'lib/library';
import T from 'components/common/text/T';
import TeethSvgForPdf from 'components/common/teeth/clinic/TeethSvgForPdf';
import { IndicationPart, IProjectInfo, Material } from 'lib/types/projectTypes';
import { IUserProfile } from 'lib/types/userTypes';
import { genderList } from 'lib/mapper';
import { IRemake, ITooth } from 'lib/types/clinic/projectTypes';
import { useTranslation } from 'react-i18next';

const styles = StyleSheet.create({
  section: { color: 'white', textAlign: 'center', margin: 30 },
  headerSection: {
    width: '100%',
    height: 138,
    backgroundColor: 'rgba(204, 237, 249, 0.30)',
    padding: 30,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 25,
  },
  headerLeft: {
    paddingTop: 10,
  },
  headerLogoBox: {
    width: 70,
    marginBottom: 18,
  },
  headerRight: { flexDirection: 'column' },
  headerTitleBox: {
    paddingTop: 30,
    height: 138,
    width: 315,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    overflowWrap: 'anywhere',
    textAlign: 'right',
  },
  headerAddressBox: {},
  headerAddress: { flexDirection: 'row' },
  headerAddress2: {},
  hedaerTitleUnderLine: { marginTop: 18, width: '100%', height: 10, backgroundColor: '#CED2DF' },
  //
  projectInfoSection: {
    padding: '0 40px',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  informationBox: {},
  indicationTeethBox: { width: 140, height: '100%' },
  patientInfoBox: { marginBottom: 45 },
  orderInfoBox: { marginBottom: 45 },
  remakeBox: { marginBottom: 0 },
  remakeInfoBox: {},
  memoBox: {
    marginBottom: 30,
    width: 350,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    overflowWrap: 'anywhere',
  },
  //
  teethSection: { padding: '0 40px 45px 40px', overflow: 'hidden' },
  tableHeader: {
    padding: '7px 10px',
    backgroundColor: 'rgba(204, 237, 249, 0.40)',
  },
  tableHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  tableBody: {},
  tableRowEven: {
    padding: '5px 10px',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  tableRowOdd: {
    padding: '5px 10px',
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: 'rgba(206, 210, 223, 0.60)',
  },
  tableRowCol1: { width: 40 },
  tableRowCol2: { width: 150 },
  tableRowCol3: { width: 40 },
  tableRowCol4: { width: 150 },
  //
  textWrapper: { flexDirection: 'row' },
  textHeaderAddressLable: {
    fontSize: 10,
    color: '#444',
    fontWeight: 400,
    width: 30,
    marginRight: 5,
  },
  textHeaderAddress: {
    fontSize: 10,
    color: '#444',
    fontWeight: 400,
    width: 200,
  },
  textHeaderTitle: {
    fontSize: 32,
    color: '#25256F',
    fontWeight: 500,
  },
  textInfoTitle: {
    fontSize: 14,
    color: '#25256F',
    fontWeight: 700,
    marginBottom: 8,
  },
  textPatientInfo: {
    fontSize: 10,
    color: '#444',
    fontWeight: 400,
    marginBottom: 4,
  },
  textInfo: {
    fontSize: 10,
    color: '#444',
    fontWeight: 400,
    marginBottom: 4,
  },
  textMemo: {
    fontSize: 10,
    color: '#444',
    fontWeight: 400,
    width: 350,
  },
  textTableHeader: {
    fontSize: 14,
    color: '#25256F',
    fontWeight: 700,
  },
  textTableBody: {
    fontSize: 10,
    color: '#25256F',
    fontWeight: 400,
  },
});

interface ProductionRequestPdfProps {
  language: string;
  projectInfo: IProjectInfo;
  user: IUserProfile;
  teeth: any[];
  bridge: any[];
  partList: IndicationPart[];
  materialList: Material[];
}
const ProductionRequestPdf = ({
  language,
  projectInfo,
  user,
  teeth,
  bridge,
  partList,
  materialList,
}: ProductionRequestPdfProps) => {
  const { t } = useTranslation();
  const userMemo = useMemo(() => {
    const memosplit = projectInfo.senderMemo?.split('\n');
    const memo: string[] = [];
    memosplit?.forEach(async (item: string) => {
      const size = 40;
      const split = Math.floor(item.length / size) + 1;
      for (let i = 0; i < split; i++) {
        memo.push(item.slice(i * size, (i + 1) * size));
      }
    });
    return memo;
  }, [projectInfo]);

  const scanDate = useMemo(() => {
    if (!projectInfo.timeline?.scan) {
      return '-';
    } else if (language === 'en') {
      return moment.unix(projectInfo.timeline?.scan).locale(language).format('MMM DD, YYYY');
    } else {
      return moment.unix(projectInfo.timeline?.scan).locale(language).format('YYYY.MM.DD');
    }
  }, [projectInfo, language]);

  const completeDueDate = useMemo(() => {
    if (!projectInfo.dueDate) {
      return '-';
    } else if (language === 'en') {
      return moment.unix(projectInfo.dueDate).locale(language).format('MMM DD, YYYY');
    } else {
      return moment.unix(projectInfo.dueDate).locale(language).format('YYYY.MM.DD');
    }
  }, [projectInfo, language]);

  const indiactionList: any[] =
    useMemo(() => {
      return teeth?.map((item: ITooth) => {
        const indicationName = partList?.find(
          (part: IndicationPart) => part.alias === item.indicationAlias,
        )?.name;
        const materialName = materialList?.find(
          (material: Material) => material.alias === item.materialAlias,
        )?.name;

        return { ...item, indicationName, materialName };
      });
    }, [teeth, partList, materialList]) || [];

  const remakes: IRemake[] =
    useMemo(() => {
      return projectInfo.ownerRemakeType;
    }, [projectInfo]) || [];

  return (
    <>
      <View style={styles.headerSection} fixed>
        <View style={styles.headerLeft}>
          <Image src={logo_dof} style={styles.headerLogoBox} />
          <View style={styles.headerAddressBox}>
            <View style={{ ...styles.headerAddress, marginBottom: 5 }}>
              <Text style={styles.textHeaderAddressLable}>{'Addr.'}</Text>
              <Text style={{ ...styles.textHeaderAddress }}>
                {`${!!user?.city ? user.city : ''} ${!!user?.district ? user.district : ''} ${
                  !!user?.address ? user.address : ''
                }`}
              </Text>
            </View>
            <View style={styles.headerAddress}>
              <Text style={styles.textHeaderAddressLable}>{'FAX.'}</Text>
              <Text style={styles.textHeaderAddress}>{!!user?.phone ? user.phone : ''}</Text>
            </View>
          </View>
        </View>
        <View style={styles.headerRight}>
          <View style={styles.headerTitleBox}>
            <Text style={styles.textHeaderTitle}>{t('PRODUCTION_REQUEST_TITLE')}</Text>
          </View>
        </View>
      </View>
      <View style={styles.projectInfoSection}>
        <View style={styles.informationBox}>
          <View style={styles.patientInfoBox}>
            <Text style={styles.textInfoTitle}>
              <T>PRODUCTION_REQUEST_PATIENT_INFO</T>
            </Text>
            <Text
              style={styles.textInfo}
            >{`${projectInfo?.patientInfo?.lastName}${projectInfo?.patientInfo?.firstName}`}</Text>
            <Text style={styles.textInfo}>
              {!!calculateAge(projectInfo.patientInfo?.birthday).age ||
              calculateAge(projectInfo.patientInfo?.birthday).age === 0
                ? calculateAge(projectInfo.patientInfo?.birthday).age
                : '-'}
            </Text>
            <Text style={{ ...styles.textInfo, marginBottom: 0 }}>
              {genderList[projectInfo?.patientInfo?.gender as keyof typeof genderList]}
            </Text>
          </View>
          <View style={styles.orderInfoBox}>
            <Text style={styles.textInfoTitle}>
              <T>PRODUCTION_REQUEST_ORDER_INFO</T>
            </Text>
            <Text style={styles.textInfo}>{projectInfo?.syncInfo?.partnerName}</Text>

            <View style={styles.textWrapper}>
              <Text style={styles.textInfo}>
                <T>PRODUCTION_REQUEST_SCAN_DATE</T>
              </Text>
              <Text style={{ ...styles.textInfo, margin: '0 5px' }}>{':'}</Text>
              <Text style={styles.textInfo}>{scanDate}</Text>
            </View>
            <View style={styles.textWrapper}>
              <Text style={{ ...styles.textInfo, color: '#00A6E2' }}>
                <T>PRODUCTION_REQUEST_DUE_DATE</T>
              </Text>
              <Text style={{ ...styles.textInfo, color: '#00A6E2', margin: '0 5px' }}>{':'}</Text>
              <Text style={{ ...styles.textInfo, color: '#00A6E2' }}>{completeDueDate}</Text>
            </View>
            <View style={{ ...styles.remakeBox, ...styles.textWrapper }}>
              <Text style={{ ...styles.textInfo }}>
                <T>GLOBAL_REMAKE</T>
              </Text>
              <Text style={{ ...styles.textInfo, margin: '0 5px' }}>{':'}</Text>
              <View style={styles.remakeInfoBox}>
                {remakes.length > 0 ? (
                  remakes.map((item: IRemake, idx: number) => {
                    return (
                      <Text key={idx} style={{ ...styles.textInfo }}>
                        {item.remakeTypeName}
                      </Text>
                    );
                  })
                ) : (
                  <Text style={{ ...styles.textInfo }}>
                    <T>GLOBAL_NONE</T>
                  </Text>
                )}
              </View>
            </View>
          </View>
          <View style={styles.memoBox}>
            <Text style={styles.textInfoTitle}>
              <T>PROJECT_MEMO</T>
            </Text>
            {userMemo?.map((item: string, idx: number) => {
              return (
                <Text key={idx} style={styles.textMemo}>
                  {item}
                </Text>
              );
            })}
          </View>
        </View>
        <View style={styles.indicationTeethBox}>
          <TeethSvgForPdf teeth={teeth} bridge={bridge} />
        </View>
      </View>
      <View style={styles.teethSection}>
        <View style={styles.tableHeader} fixed>
          <View style={styles.tableHeaderRow}>
            <View style={styles.tableRowCol1}>
              <Text style={styles.textTableHeader}>
                <T>PRODUCTION_REQUEST_NUMBER</T>
              </Text>
            </View>
            <View style={styles.tableRowCol2}>
              <Text style={styles.textTableHeader}>
                <T>PRODUCTION_REQUEST_INDICATION</T>
              </Text>
            </View>
            <View style={styles.tableRowCol3}>
              <Text style={styles.textTableHeader}>
                <T>PRODUCTION_REQUEST_SHADE</T>
              </Text>
            </View>
            <View style={styles.tableRowCol4}>
              <Text style={styles.textTableHeader}>
                <T>PRODUCTION_REQUEST_MATERIAL</T>
              </Text>
            </View>
          </View>
        </View>
        <View style={styles.tableBody}>
          {indiactionList?.map((item, idx) => {
            const rowStyle = !!(idx % 2) ? styles.tableRowOdd : styles.tableRowEven;
            return (
              <View style={rowStyle} key={idx}>
                <View style={styles.tableRowCol1}>
                  <Text style={styles.textTableBody}>{item.number}</Text>
                </View>
                <View style={styles.tableRowCol2}>
                  <Text style={styles.textTableBody}>{item.indicationName}</Text>
                </View>
                <View style={styles.tableRowCol3}>
                  <Text style={styles.textTableBody}>{item.shade.code}</Text>
                </View>
                <View style={styles.tableRowCol4}>
                  <Text style={styles.textTableBody}>{item.materialName}</Text>
                </View>
              </View>
            );
          })}
        </View>
      </View>
    </>
  );
};

export default React.memo(ProductionRequestPdf);
